import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { pdfjsLib } from '@/lib/pdfWorker';
import { extractOverlayMetadata, tagApCarriedOverlays } from '@/lib/overlayMetadata';
import { stripScrivarAnnotsToBytes } from '@/lib/pdfAnnotations';
import { pageGeometry } from '@/lib/pageGeometry';

const LARGE_FILE_THRESHOLD = 500;
const THUMB_DISABLE_THRESHOLD = 200;

/**
 * usePDF — Core hook for loading and interacting with a PDF document.
 *
 * Exposes size-tier flags so the viewer can adapt its UI:
 *   isLargeFile  — >500 pages, single-page mode (no scroll navigation)
 *   showThumbs   — <=200 pages, render thumbnail sidebar
 *
 * Exposes pageSize (page 1 dimensions at scale=1) for placeholder sizing.
 */
export default function usePDF() {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1.0);
  const [isFitted, setIsFitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState('');
  const [pageSize, setPageSize] = useState({ width: 612, height: 792 });
  const [pageSizes, setPageSizes] = useState({});
  const [maxPageWidth, setMaxPageWidth] = useState(612);
  // Scrivar editable-overlay sidecar (edits + annotations) extracted from a file
  // WE saved. Non-null only for our own files; the Viewer restores it into the
  // editable layer while the canvas renders from the scrivar-stripped bytes.
  const [sidecar, setSidecar] = useState(null);
  const docRef = useRef(null);
  const loadGeneration = useRef(0);
  const pageWidthRef = useRef(612);
  const boundsRef = useRef([]);

  const isLargeFile = useMemo(() => numPages > LARGE_FILE_THRESHOLD, [numPages]);
  const showThumbs = useMemo(() => numPages <= THUMB_DISABLE_THRESHOLD, [numPages]);

  const loadFile = useCallback(async (file) => {
    const generation = ++loadGeneration.current;
    setLoading(true);
    setError(null);
    setFileName(file.name);

    try {
      const arrayBuffer = await file.arrayBuffer();

      // Detect a Scrivar editable-overlay sidecar (only present in files WE
      // saved). When present, render the canvas from a copy with our OWN
      // annots (/NM scrivar:) stripped — otherwise PDF.js would paint them on
      // the canvas AND the Viewer would restore them into the EditLayer, which
      // is the double-render bug. pdf-lib's load can detach the buffer it's
      // given, so feed each consumer its own slice(0).
      let metadata = null;
      let bounds = [];
      try {
        const { PDFDocument } = await import('pdf-lib');
        const probeDoc = await PDFDocument.load(arrayBuffer.slice(0), { ignoreEncryption: true });
        bounds = probeDoc.getPages().map((page) => page.getSize());
        metadata = await extractOverlayMetadata(probeDoc);
      } catch (_) {
        metadata = null;
      }
      const hasSidecar = !!(metadata && ((metadata.edits?.length) || (metadata.annotations?.length)));

      let renderBytes = arrayBuffer;
      if (hasSidecar) {
        try {
          const stripped = await stripScrivarAnnotsToBytes(arrayBuffer.slice(0));
          renderBytes = stripped.bytes;
          // Overlays whose /AP annot was just stripped have NO pixels baked in
          // the content stream — tag them so restore doesn't fabricate
          // bakedBounds (which would white-mask raw page pixels on scans).
          metadata = tagApCarriedOverlays(metadata, stripped.apCarriedIds);
        } catch (_) {
          // If the strip fails, fall back to faithful render of the original
          // bytes (canvas shows our annots; no restore) rather than crashing.
          renderBytes = arrayBuffer;
          metadata = null;
        }
      }
      if (generation !== loadGeneration.current) return;

      const doc = await pdfjsLib.getDocument({ data: renderBytes }).promise;
      if (generation !== loadGeneration.current) { await doc.destroy(); return; }

      const page1 = await doc.getPage(1);
      const viewport = page1.getViewport({ scale: 1.0 });
      const sizes = { 1: pageGeometry(page1, bounds[0]) };

      // Use the widest page for fit-to-width calculation
      let maxWidth = viewport.width;
      if (doc.numPages <= 500) {
        for (let i = 2; i <= doc.numPages; i++) {
          const p = await doc.getPage(i);
          const vp = p.getViewport({ scale: 1.0 });
          sizes[i] = pageGeometry(p, bounds[i - 1]);
          if (vp.width > maxWidth) maxWidth = vp.width;
        }
      }
      if (generation !== loadGeneration.current) { await doc.destroy(); return; }
      setPageSize(sizes[1]);
      setPageSizes(sizes);
      boundsRef.current = bounds;
      const previous = docRef.current;
      docRef.current = doc;
      if (previous) previous.destroy();
      setSidecar(hasSidecar ? metadata : null);
      pageWidthRef.current = maxWidth;
      setMaxPageWidth(maxWidth);

      setPdfDoc(doc);
      setNumPages(doc.numPages);
      setCurrentPage(1);
      setZoom(1.0);
      setIsFitted(false);
    } catch (err) {
      if (generation !== loadGeneration.current) return;
      setError(`Failed to load PDF: ${err.message}`);
      setPdfDoc(null);
      setNumPages(0);
      setSidecar(null);
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, []);

  const clearSidecar = useCallback(() => setSidecar(null), []);

  useEffect(() => {
    if (!pdfDoc || pageSizes[currentPage]) return;
    let cancelled = false;
    pdfDoc.getPage(currentPage).then((page) => {
      if (cancelled) return;
      setPageSizes((sizes) => ({ ...sizes, [currentPage]: pageGeometry(page, boundsRef.current[currentPage - 1]) }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [pdfDoc, currentPage, pageSizes]);

  const goToPage = useCallback((page) => {
    setCurrentPage((prev) => {
      const target = typeof page === 'function' ? page(prev) : page;
      return Math.max(1, Math.min(target, numPages));
    });
  }, [numPages]);

  const nextPage = useCallback(() => goToPage((p) => p + 1), [goToPage]);
  const prevPage = useCallback(() => goToPage((p) => p - 1), [goToPage]);

  const zoomIn = useCallback(() => {
    setIsFitted(false);
    setZoom((z) => Math.min(z + 0.1, 5.0));
  }, []);

  const zoomOut = useCallback(() => {
    setIsFitted(false);
    setZoom((z) => Math.max(z - 0.1, 0.25));
  }, []);

  const fitToWidth = useCallback((containerWidth) => {
    if (!pageWidthRef.current) return;
    const scale = Math.round((containerWidth / pageWidthRef.current) * 100) / 100;
    setZoom(scale);
    setIsFitted(true);
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1.0);
    setIsFitted(false);
  }, []);

  useEffect(() => {
    return () => {
      loadGeneration.current += 1;
      if (docRef.current) {
        docRef.current.destroy();
      }
    };
  }, []);

  return {
    pdfDoc,
    numPages,
    currentPage,
    zoom,
    isFitted,
    isLargeFile,
    showThumbs,
    pageSize: pageSizes[currentPage] || pageSize,
    pageSizes,
    maxPageWidth,
    loading,
    error,
    fileName,
    setFileName,
    sidecar,
    clearSidecar,
    loadFile,
    goToPage,
    nextPage,
    prevPage,
    zoomIn,
    zoomOut,
    fitToWidth,
    resetZoom,
    setZoom,
    setIsFitted,
  };
}
